import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV } from "@/constants/testIds";
import { PAYHIP_STORE_URL } from "@/lib/config";

const links = [
  { id: "about", label: "About", testId: NAV.linkAbout },
  { id: "pillars", label: "Pillars", testId: NAV.linkPillars },
  { id: "collection", label: "Collection", testId: NAV.linkCollection },
  { id: "journal", label: "Journal", testId: NAV.linkJournal },
  { id: "contact", label: "Contact", testId: NAV.linkContact },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a0a0a]/80 backdrop-blur-2xl border-b border-[#c9d3d8]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        <a
          href="#top"
          data-testid={NAV.logo}
          onClick={handleAnchor("top")}
          className="flex items-center gap-2 select-none"
          aria-label="VISION home"
        >
          <Logo
            className="h-11 md:h-14 w-36 md:w-52"
            color="#e8ecef"
          />
        </a>

        <div className="hidden md:flex items-center gap-12">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={handleAnchor(l.id)}
              data-testid={l.testId}
              className="text-[12px] tracking-[0.22em] uppercase text-[#c9d3d8]/85 hover:text-[#e8ecef] transition-colors duration-500"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:block">
          <a
            href={PAYHIP_STORE_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={NAV.shopBtn}
            className="inline-flex items-center gap-2 bg-[#e8ecef] text-[#0a0a0a] px-7 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#c9d3d8] transition-colors duration-500"
          >
            Shop
          </a>
        </div>

        <button
          className="md:hidden text-[#e8ecef] p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid={NAV.mobileMenu}
          className="md:hidden bg-[#0a0a0a]/95 backdrop-blur-2xl border-t border-[#c9d3d8]/10"
        >
          <div className="flex flex-col px-6 py-10 gap-7">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={handleAnchor(l.id)}
                data-testid={`${l.testId}-mobile`}
                className="text-[#e8ecef]/85 text-sm tracking-[0.22em] uppercase"
              >
                {l.label}
              </a>
            ))}
            <a
              href={PAYHIP_STORE_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`${NAV.shopBtn}-mobile`}
              className="mt-2 inline-flex items-center justify-center bg-[#e8ecef] text-[#0a0a0a] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase"
            >
              Shop Collection
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
